import { memo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FiBookmark, FiCheck, FiEyeOff, FiFileText } from 'react-icons/fi';
import type { PdfItem } from '../../types/pdf';
import { formatBytes, formatDate } from '../../utils/format';
import { BookmarkPicker } from '../viewer/BookmarkPicker';

interface Props {
  item: PdfItem;
  active: boolean;
  bookmarked: boolean;
  onSelect: () => void;
  onHide: () => void;
}

export const PdfCard = memo(function PdfCard({ item, active, bookmarked, onSelect, onHide }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const bookmarkButtonRef = useRef<HTMLButtonElement>(null);
  const touch = useRef<{ pointerId: number; x: number; y: number } | undefined>(undefined);
  const lastTouchSelection = useRef(0);

  return (
    <motion.article
      layout
      whileHover={{ y: -2 }}
      transition={{ duration: 0.16 }}
      className={`pdf-card ${active ? 'pdf-card-active' : ''}`}
      onPointerDown={(event) => {
        if (event.pointerType === 'touch')
          touch.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      }}
      onPointerUp={(event) => {
        const start = touch.current;
        touch.current = undefined;
        if (event.pointerType !== 'touch' || !start || start.pointerId !== event.pointerId) return;
        if (Math.hypot(event.clientX - start.x, event.clientY - start.y) <= 12) {
          lastTouchSelection.current = Date.now();
          onSelect();
        }
      }}
      onPointerCancel={() => {
        touch.current = undefined;
      }}
      onClick={() => {
        if (Date.now() - lastTouchSelection.current > 500) onSelect();
      }}
      tabIndex={0}
      role="button"
      aria-pressed={active}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="pdf-thumb">
        {item.thumbnail ? (
          <img src={item.thumbnail} loading="lazy" alt="" draggable={false} />
        ) : (
          <div className="paper-preview">
            <span className="paper-line w-3/5" />
            <span className="paper-line" />
            <span className="paper-line w-4/5" />
            <FiFileText />
          </div>
        )}
        {item.recent && <span className="recent-badge">Recent</span>}
      </div>
      <div className="pdf-card-details min-w-0 flex-1">
        <h3 title={item.name.replace(/\.pdf$/i, '')}>{item.name.replace(/\.pdf$/i, '')}</h3>
        <p>{item.pages} pages · {formatBytes(item.size)}</p>
        <p className="date">
          {item.libraryName ? `${item.libraryName} · ` : ''}
          {formatDate(item.modifiedAt)}
        </p>
      </div>
      <button
        ref={bookmarkButtonRef}
        className={`favorite bookmark-action ${bookmarked ? 'favorite-on' : ''}`}
        aria-label="Choose bookmark collections"
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          setPickerOpen(true);
        }}
      >
        <FiBookmark fill={bookmarked ? 'currentColor' : 'none'} />
      </button>
      <button className="hide-action" aria-label="Hide PDF" title="Hide PDF" onPointerDown={(event) => event.stopPropagation()} onPointerUp={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); if (window.confirm(`Hide “${item.name}” from the library?`)) onHide(); }}><FiEyeOff /></button>
      {active && <span className="pdf-card-selected" aria-label="Selected"><FiCheck /> Selected</span>}
      {pickerOpen && <BookmarkPicker documentId={item.id} anchorRef={bookmarkButtonRef} onClose={() => setPickerOpen(false)} />}
    </motion.article>
  );
});
